import React, { useCallback } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import Details from '../MUI/SummaryWrapper/Details';
import MainButton from '../MUI/MainButton';
import SummaryWrapper from '../MUI/SummaryWrapper';
import TitleTypography from '../MUI/TitleTypography';
import { removeFromBasket } from '../../redux/modules/basketReducer';
import { getPaymentDetails, getBasketDetails } from '../../helpers/policy/bindData';
import { setContract } from '../../redux/modules/recPayment';
import { AMD_SYMBOL } from '../../constants';
import { RECURRING_SCHEDULE_PATH } from '../../constants/routes';
import useDeviceResize from '../../hooks/useDeviceResize';

import useStyles from '../MUI/SummaryWrapper/makeStyles';

const PolicySummary = ({
  openMobileSummary,
  onDismiss,
  handlePayClick,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDeviceLgSize = useDeviceResize('lg');
  const history = useHistory();
  const classes = useStyles();

  const { basket } = useSelector((store) => store.basketReducer, shallowEqual);
  const { contract } = useSelector((store) => store.recPayment, shallowEqual);
  const totalAndBalance = contract && contract.totalAndBalance;
  const selectedToPay = totalAndBalance && totalAndBalance.selectedToPay;
  const total = totalAndBalance && totalAndBalance.total;
  const generalAmount = basket.reduce(
    (acc, curr) => acc + curr.totalAndBalance.selectedToPay,
    0,
  );
  const selectedContract = contract
    && basket.find((el) => el.policyNumber === contract.policyNumber);

  const handleRowClick = (c) => {
    dispatch(setContract(c));
  };

  const handleCloseIconClick = useCallback((pNumber) => {
    dispatch(removeFromBasket(pNumber));
    if (basket.length === 1) {
      history.push(RECURRING_SCHEDULE_PATH);
    }
  }, [dispatch, basket.length, history]);

  const paymentDetails = getPaymentDetails(total, selectedToPay, t);

  const basketDetails = getBasketDetails(
    basket,
    selectedContract,
    handleRowClick,
    handleCloseIconClick,
  );

  return (
    <SummaryWrapper openMobileSummary={openMobileSummary} onDismiss={onDismiss}>
      <div className={classes.policySummaryContainer}>
        <Grid item xs={12} className={classes.sectionBox}>
          <TitleTypography component="div" className={classes.sectionTitle}>
            {t('recurrPayment.summary')}
          </TitleTypography>
          <Grid item className={cn(classes.sectionRow, classes.unsetPaddingLeft)}>
            <Details details={paymentDetails} />
          </Grid>
        </Grid>

        <Grid item xs={12} className={classes.sectionBox}>
          <Grid container sapcing={2} justifyContent="space-between">
            <Grid item>
              <TitleTypography component="div" className={classes.sectionTitle}>
                {t('recurrPayment.basket')}
              </TitleTypography>
            </Grid>
            <Grid item>
              <Typography component="div">
                {generalAmount <= 0 ? t('recurrPayment.empty') : basketDetails.length}
              </Typography>
            </Grid>
          </Grid>
          {
            !!basketDetails.length && (
              <Grid
                item
                className={cn(
                  classes.sectionRow,
                  classes.basketDetails,
                )}
              >
                <Details details={basketDetails} />
              </Grid>
            )
          }
        </Grid>
        {!isDeviceLgSize && (
          <Grid item xs={12} className={classes.sectionBox}>
            <Grid item className={cn(classes.sectionRow, classes.unsetPaddingLeft)}>
              <MainButton
                variant="contained"
                color="primary"
                onClick={handlePayClick}
                className={classes.buyButton}
                disabled={generalAmount <= 0}
                width="fullWidth"
              >
                {t('pay')}
                {!!generalAmount && !isDeviceLgSize && (
                  <>
                    &nbsp;
                    <b>{`${AMD_SYMBOL} ${generalAmount.toLocaleString('en-US')}`}</b>
                  </>
                )}
              </MainButton>
            </Grid>
          </Grid>
        )}
      </div>
    </SummaryWrapper>
  );
};

PolicySummary.propTypes = {
  openMobileSummary: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  handlePayClick: PropTypes.func,
};

PolicySummary.defaultProps = {
  openMobileSummary: false,
  handlePayClick: () => { },
};

export default PolicySummary;
